/**
 * "Debt Summary" card (design §8, full-screening only): Monthly / Total
 * Owing header stat chips + three equal sub-cards per debt type. The
 * utilization bar fill is DARK CHARCOAL on a light gray track (v1
 * DebtSummarySection.tsx:109-114 parity — NOT the header score's green).
 * Icon/tone per debt type is a recorded design extrapolation (the export
 * shows briefcase/car/graduation-cap for its three types); unknown types
 * fall back to a credit-card icon and cycle the pastel tones. Renders null
 * when the payload carries no debtSummary (v1 presence parity, INSIDE the
 * page's kind gate — data-map §6).
 */
import { BriefcaseBusiness, Car, CreditCard, GraduationCap } from 'lucide-react'
import type { ReactNode } from 'react'
import {
	type DebtAccountView,
	deriveDebtSummaryView,
} from '#app/utils/application-detail-v2/report-display'
import { cn, formatCurrency } from '#app/utils/misc'
import {
	DETAIL_CARD,
	IconSquare,
	type PastelTone,
	SectionIcon,
} from '../primitives'
import { StatChip, money } from './report-bits'

const TONE_CYCLE: PastelTone[] = ['lrose', 'blue', 'mint', 'amber']

/** Export-matched icon/tone per known type; cycled tones for the rest. */
function debtTypeStyle(
	type: string,
	index: number,
): { icon: ReactNode; label: string; tone: PastelTone } {
	const lower = type.toLowerCase()
	if (/\b(open|revolving)\b/.test(lower))
		return {
			icon: <BriefcaseBusiness />,
			label: 'Open & Revolving',
			tone: 'lrose',
		}
	if (/\b(auto|automobile|car|vehicle)\b/.test(lower))
		return { icon: <Car />, label: 'Automobile Loans', tone: 'blue' }
	if (/\b(student|education)\b/.test(lower))
		return { icon: <GraduationCap />, label: 'Student Loans', tone: 'mint' }
	return {
		icon: <CreditCard />,
		label: type || '-',
		tone: TONE_CYCLE[index % TONE_CYCLE.length],
	}
}

/** Bordered centered label-over-value box (Monthly Payments / Past Due /
 * Payment Status, spec §8 items 4-5). */
function StatBox({
	label,
	value,
	valueClassName,
}: {
	label: string
	value: string
	valueClassName?: string
}) {
	return (
		<div className="rounded-xl px-2 py-3 text-center shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]">
			<span className="block text-sm text-[#747474]">{label}</span>
			<span
				className={cn(
					'mt-1 block text-[17px] font-medium text-[#32302F]',
					valueClassName,
				)}
			>
				{value}
			</span>
		</div>
	)
}

function DebtCard({
	detail,
	index,
}: { detail: DebtAccountView; index: number }) {
	const { icon, label, tone } = debtTypeStyle(detail.type, index)
	const percent = Math.min(Math.max(detail.utilizationPercent ?? 0, 0), 100)
	const utilizationLabel =
		detail.used ||
		(detail.utilizationPercent != null ? `${detail.utilizationPercent}%` : '')

	return (
		<div className="rounded-2xl border border-[#F0F0F0] bg-[#FBFDFD] p-5">
			<div className="flex items-center gap-3">
				<IconSquare tone={tone} className="size-11">
					{icon}
				</IconSquare>
				<h3 className="flex-1 text-[17px] font-medium text-[#32302F]">
					{label}
				</h3>
				{utilizationLabel && (
					<span className="text-[18px] font-medium text-[#32302F]">
						{utilizationLabel}
					</span>
				)}
			</div>
			<div
				role="progressbar"
				tabIndex={-1}
				aria-valuenow={percent}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-label={`${detail.type || 'Debt'} utilization`}
				className="mt-5 h-2 w-full rounded-full bg-[#F0F0F0]"
			>
				<div
					className="h-2 rounded-full bg-[#32302F]"
					style={{ width: `${percent}%` }}
				/>
			</div>
			<div className="mt-2.5 flex items-center justify-between text-sm">
				<span className="font-medium text-[#747474]">
					{money(detail.balance)} <span className="font-normal">Balance</span>
				</span>
				<span className="font-medium text-[#747474]">
					{money(detail.limit)} <span className="font-normal">Limit</span>
				</span>
			</div>
			<div className="mt-4">
				<StatBox
					label="Monthly Payments"
					value={money(detail.monthlyPayments)}
				/>
			</div>
			<div className="mt-3 grid grid-cols-2 gap-3">
				<StatBox label="Past Due" value={money(detail.pastDue)} />
				<StatBox
					label="Payment Status"
					value={detail.paymentStatus || '-'}
					valueClassName="capitalize"
				/>
			</div>
		</div>
	)
}

export function DebtSummary({
	aiReportData,
}: {
	aiReportData: Record<string, unknown> | null
}) {
	const view = deriveDebtSummaryView(aiReportData)
	if (!view) return null

	return (
		<section className={cn(DETAIL_CARD, 'p-6 sm:p-7')}>
			<div className="flex flex-wrap items-center justify-between gap-4">
				<div className="flex items-center gap-3">
					<SectionIcon src="/img/debt-summary-icon.svg" />
					<h2 className="text-[19px] font-semibold text-[#32302F]">
						Debt Summary
					</h2>
				</div>
				<div className="flex flex-wrap items-center gap-3">
					{view.monthlyDebtPayment != null && (
						<StatChip
							label="Monthly"
							value={formatCurrency(view.monthlyDebtPayment)}
						/>
					)}
					{view.totalDebtOwing != null && (
						<StatChip
							label="Total Owing"
							value={formatCurrency(view.totalDebtOwing)}
						/>
					)}
				</div>
			</div>
			{view.details.length > 0 && (
				<div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
					{view.details.map((detail, idx) => (
						<DebtCard key={idx} detail={detail} index={idx} />
					))}
				</div>
			)}
		</section>
	)
}
